import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import { getAdminAreaByIdApi } from "@/api/adminArea/adminAreaApi";
import {
  optimizeSelectValue,
  useGetAnalysisConfigMutation,
  useGetConfigurationByID,
  useSaveConfigurationMutation
} from "@/api/configuration/useConfigurationAPI";
import { fetchDatasetStream } from "@/api/dataset/fetchDatasetStream";
import {
  useGetDatasetDataMutation,
  useMutationDatasetMetadata
} from "@/api/dataset/useDatasetAPI";
import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import SaveSuccessNotiContent from "@/components/spatialAnalysis/notification/saveConfigurationSuccess";
import { ConfigurationFormData } from "@/components/spatialAnalysis/saveConfigurationPopup";
import { MESSAGE } from "@/constants/message";
import { buildFilterFromResponse } from "@/helpers/filterHelper";
import { getDisplayErrorMessage } from "@/helpers/handleErrorResponse";
import { RootState } from "@/store";
import {
  recalculateWeightPercentages,
  setDatasetHistory
} from "@/store/slice/datasetSlice";
import {
  setAdminAreaSettings as setGlobalAdminAreaSettings,
  setAdminAreaSettingsData as setGlobalAdminAreaSettingsData,
  setGlobalAdminArea,
  setSelectedAreaIds as setGlobalSelectedAreaIds
} from "@/store/slice/globalFilterSlice";
import { setSpatialState } from "@/store/slice/spatialSlice";
import {
  EspatialAggregationType,
  SpatialAggregationType
} from "@/types/common";
import {
  DatasetData,
  DatasetDetailResponse,
  DatasetHistory,
  DatasetIndices,
  DatasetMetadata
} from "@/types/dataset";
import {
  AnalysisConfigurationDetail,
  ConfigurationPayload,
  DatasetConfigAttributeResponse,
  DatasetConfigResponse
} from "@/types/project";
import { ECatchmentInputBy } from "@/types/spatial";
import { CustomError } from "@/utils/errors";

import { useMap } from "../map/core/useMapContext";
import { useDataset } from "../map/useDataset";
import useHandleClearView from "../ui/useHandleClearView";
import useToast, { EToastType } from "../ui/useToast";
import {
  getDeletedDatasetsName,
  mapDatasetWeights,
  mapSpatialState
} from "../useAnalysisConfiguration";
import { useAppFeatures } from "../useAppFeatures";
import useConfigurationMappers from "./useConfigurationMapper";
import { buildDatasetFilter, getAggregationConfig } from "./utils";

type UseConfigurationProps = {
  onSaveConfigSuccess?: () => void;
  onSaveConfigError?: (error: CustomError) => void;
  onOpenConfigSuccess?: (
    configurationDetailData: AnalysisConfigurationDetail
  ) => void;
  onOpenConfigError?: (error: CustomError) => void;
  setDuplicateNameOnSaveConfig?: (value: boolean) => void;
  onOpenProjectFromToast?: () => void;
};

export const useConfiguration = ({
  onSaveConfigSuccess,
  onSaveConfigError,
  onOpenConfigSuccess,
  onOpenConfigError,
  onOpenProjectFromToast
}: UseConfigurationProps) => {
  const dispatch = useDispatch();
  const {
    isRefactorAddDatasetEnabled,
    isAdminTreeEnabled,
    isBVTDataForCatchmentEnabled
  } = useAppFeatures();
  const {
    handleAddDataset,
    handleAddDeletedDataset,
    handleCreateDatasetHistory,
    generateDatasetData
  } = useDataset();
  const { showToast } = useToast();
  const {
    mappingAnalysisOutput,
    mappingAreaInput,
    mappingDataset,
    mappingAdmAreaIds
  } = useConfigurationMappers();
  const { handleCloseDetailView } = useHandleClearView();
  const { addFeature } = useMap();
  const { data: userProfileData } = useGetUserProfileAPI();
  const { latestAnalysisId } = useSelector((state: RootState) => state.spatial);

  const spatial = useSelector((state: RootState) => state.spatial);

  const handleOpenProjectFromToast = (data: AnalysisConfigurationDetail) => {
    if (!data?.id) return;
    handleCloseDetailView();
    onOpenProjectFromToast?.();
  };

  const { mutate: mutateSaveConfig } = useSaveConfigurationMutation({
    onMutate: () => {
      toast.dismiss();
      showToast({
        type: EToastType.LOADING,
        message: MESSAGE.SAVING_NEW_PROJECT
      });
    },
    onSuccess: (data, payload) => {
      toast.dismiss();
      showToast({
        type: EToastType.SUCCESS,
        message: (
          <SaveSuccessNotiContent
            title="Configuration successfully saved"
            description="You can check you project by clicking link below"
            buttonLabel="Open Saved Configuration"
            onOpen={() => handleOpenProjectFromToast(data)}
          />
        ),
        autoClose: false
      });
      // Need title and description for export summary analysis
      dispatch(
        setSpatialState({
          ...spatial,
          isEdit: true,
          currentConfigId: data.id,
          title: payload.name,
          description: payload.description
        })
      );

      onSaveConfigSuccess?.();
    },
    onError: (error) => {
      showToast({
        type: EToastType.ERROR,
        message: getDisplayErrorMessage(error)
      });
      onSaveConfigError?.(error);
    }
  });

  const { mutateAsync: getDatasetDataPost } = useGetDatasetDataMutation({
    timezone: userProfileData?.timezone
  });
  const { mutateAsync: getDatasetMetadata } = useMutationDatasetMetadata();

  /**
   * Handle saving the configuration.
   * @param basicInfo - The basic configuration form data.
   */
  const handleSaveConfiguration = (basicInfo: ConfigurationFormData) => {
    const payload: ConfigurationPayload = {
      ...basicInfo,
      adm_area_ids: mappingAdmAreaIds(),
      datasets: mappingDataset(),
      area_input: mappingAreaInput(),
      analysis_output: mappingAnalysisOutput()
    };
    mutateSaveConfig(payload);
  };

  const getDatasetDataAndMetadata = async (
    reversedDatasets: DatasetConfigResponse[]
  ) => {
    const datasetsData: DatasetData[] = [];
    const datasetsDetail: DatasetMetadata[] = [];
    const datasetsDetailAggregated: DatasetMetadata[] = [];

    // Fetch dataset metadata and data sequentially
    for (const dataset of reversedDatasets) {
      const currentDatasetMetadata = await getDatasetMetadata({
        id: dataset.id
      });

      const filters = buildDatasetFilter(
        dataset.attributes,
        currentDatasetMetadata?.schema
      );

      // For H3 dataset with data source Bvarta, we need to get the aggregated schema
      if (
        currentDatasetMetadata?.spatial_aggregation_type ===
          EspatialAggregationType.H3 &&
        currentDatasetMetadata?.source === "bvt"
      ) {
        const attributeSchema = await getDatasetMetadata({
          id: currentDatasetMetadata?.id,
          h3Configuration: getAggregationConfig(dataset?.grid_config),
          show_id: false,
          group_by: dataset.group_by
        });

        datasetsDetailAggregated.push(attributeSchema);
      } else {
        datasetsDetailAggregated.push(currentDatasetMetadata);
      }

      const res = await fetchDatasetStream({
        datasetId: dataset.id,
        filters,
        timezone: userProfileData?.timezone,
        h3Configuration: getAggregationConfig(dataset?.grid_config),
        group_by: dataset?.group_by,
        generateDatasetData
      });

      datasetsDetail.push(currentDatasetMetadata);
      if (res?.rawDataset) datasetsData.push(res.rawDataset);
    }

    return { datasetsData, datasetsDetail, datasetsDetailAggregated };
  };

  const handleDatasetOnOpenProject = async (
    datasets: DatasetConfigResponse[]
  ) => {
    const reversedDatasets = [...datasets].reverse();

    const { datasetsData, datasetsDetail, datasetsDetailAggregated } =
      await getDatasetDataAndMetadata(
        reversedDatasets.filter((dataset) => dataset.available)
      );

    const duplicatedDatasetCount = new Map<string, number>();
    // After all data and metadata are collected, add them to the map
    // index for reversedDatasets is used to align with datasetsData and datasetsDetail which are in reversed order, and for handle same dataset id with different filters or group_by in the same configuration
    for (const [index, dataset] of reversedDatasets.entries()) {
      const count = duplicatedDatasetCount.get(dataset.id) || 0;
      duplicatedDatasetCount.set(dataset.id, count + 1);
      if (dataset.available) {
        const datasetMetadata: DatasetMetadata | undefined =
          datasetsDetail.find((d) => d.id === dataset.id);

        if (datasetMetadata) {
          const datasetWeights = mapDatasetWeights(
            dataset,
            datasetMetadata?.schema
          );

          await handleAddDataset({
            datasetMetadata: {
              ...datasetMetadata, // full metadata schema
              display_name: dataset?.display_name ?? dataset.name
            },
            datasetData: datasetsData[index],
            datasetIndex: duplicatedDatasetCount.get(dataset.id),
            savedFilters: buildDatasetFilter(
              dataset.attributes,
              datasetMetadata?.schema
              // Filters are stored top-to-bottom in project config, but dataset
              // history grows bottom-to-top (each entry prepended). Reversing
              // here aligns the filter order with the history display order.
            ).reverse(),
            attributesSchema: datasetsDetailAggregated[index]?.schema ?? [], // filtered metadata schema for h3 dataset
            h3Configuration: getAggregationConfig(dataset?.grid_config),
            group_by: dataset.group_by,
            weightValues: {
              // `dataset.weight` is 0 for projects saved before this field was
              // introduced. Fall back to the number of weighted attributes so
              // the weight panel initialises to a sensible non-zero value.
              weight: (
                dataset.weight ||
                Object.keys(datasetWeights).length ||
                1
              ).toString(),
              attributes: datasetWeights,
              include_scoring: dataset.include_scoring
            },
            styleConfig: {
              colorFamily: dataset?.color_scale,
              opacity: dataset?.opacity,
              selectedCategory: dataset?.color_based,
              labelConfig: dataset?.label_config,
              colorSteps: dataset?.color_steps,
              rankConfig: {
                isEnabled: dataset.label_config?.enable_ranking ?? false,
                maxRanking: dataset.label_config?.max_ranking ?? 10
              },
              strokeConfig:
                dataset.spatial_aggregation_type === "line"
                  ? {
                      width: dataset?.stroke_width ?? 1,
                      opacity: dataset?.opacity ?? 0.7
                    }
                  : undefined
            },
            is_global_admin_area: dataset?.is_global_admin_area ?? false
          });
        }
      } else {
        const deletedDatasetMetadata: DatasetDetailResponse = {
          id: dataset.id,
          name: dataset.name ?? "",
          display_name: dataset.display_name ?? "",
          country_id: "id", // arbitrary default value
          source: "bvt", // arbitrary default value
          spatial_aggregation_type:
            dataset.spatial_aggregation_type as SpatialAggregationType,
          short_description: "",
          published: false,
          created_at: "",
          updated_at: "",
          available: dataset.available,
          total_rows: 0,
          schema: []
        };
        handleAddDeletedDataset(
          deletedDatasetMetadata,
          duplicatedDatasetCount.get(dataset.id) || 1
        );
      }
    }
    // The backend config only stores `weight` per dataset/attribute, not `percentage`.
    // After all datasets are added with their weights, derive the display percentages
    // so the weight panel reflects the correct relative distribution.
    dispatch(recalculateWeightPercentages());
    return { datasetsData, datasetsDetail };
  };

  const handleCatchmentDatasetOnOpenProject = async (
    catchmentDatasetId: string,
    catchmentDatasetAttributes: DatasetConfigAttributeResponse[]
  ) => {
    const catchmentFilter = catchmentDatasetAttributes
      .filter((attr) => attr.filter?.criteria)
      .map((attr) => ({
        ...buildFilterFromResponse(attr.filter!),
        filterId: attr.id.split("_")?.[0],
        attributeCode: attr?.attribute_code ?? ""
      }));
    return {
      catchmentDatasetDetail: await getDatasetMetadata({
        id: catchmentDatasetId
      }),
      catchmentDatasetData: await getDatasetDataPost({
        id: catchmentDatasetId,
        filter: catchmentFilter
      })
    };
  };

  const handleAdmAreaOnOpenProject = async (adm_area_ids: string[]) => {
    if (adm_area_ids.length === 0) {
      return { adminAreaData: null };
    }

    const idsToFetch = isAdminTreeEnabled ? adm_area_ids : [adm_area_ids[0]];

    const adminAreaDetails = (
      await Promise.all(idsToFetch.map((id) => getAdminAreaByIdApi(id)))
    ).filter(Boolean);

    if (adminAreaDetails.length === 0) {
      return { adminAreaData: null };
    }

    // Use the first admin area detail to generate the selectValue,
    // as it represents the hierarchical selection.
    const selectValue = optimizeSelectValue(adminAreaDetails[0]);

    return {
      adminAreaData: {
        selectValue,
        geoData: {
          data: adminAreaDetails
        }
      }
    };
  };

  const getAreaInputAdmAreaIds = (
    configurationDetail: AnalysisConfigurationDetail
  ): string[] | undefined => {
    const areaInput = configurationDetail.area_input;
    return (
      areaInput?.adm_area_ids ??
      (areaInput?.adm_area_id ? [areaInput.adm_area_id] : undefined)
    );
  };

  const { mutateAsync: mutateOpenConfig, data: configurationDetailData } =
    useGetConfigurationByID({
      onMutate: () => {
        toast.dismiss();
        showToast({
          type: EToastType.LOADING,
          message: MESSAGE.LOADING_CONFIGURATION
        });
      },
      onSuccess: async (configurationDetail) => {
        const globalAdminAreaIds = configurationDetail.adm_area_ids;
        const areaInput = configurationDetail.area_input;
        const catchmentDatasetId = areaInput?.catchment?.dataset?.id;
        const catchmentDatasetAttributes =
          areaInput?.catchment?.dataset?.attributes ?? [];
        const admAreaIds = getAreaInputAdmAreaIds(configurationDetail);

        const { datasetsData, datasetsDetail } =
          isRefactorAddDatasetEnabled && configurationDetail.datasets
            ? await handleDatasetOnOpenProject(configurationDetail.datasets)
            : {};

        const { catchmentDatasetData, catchmentDatasetDetail } =
          isRefactorAddDatasetEnabled && catchmentDatasetId
            ? await handleCatchmentDatasetOnOpenProject(
                catchmentDatasetId,
                catchmentDatasetAttributes
              )
            : {};

        const { adminAreaData } =
          isRefactorAddDatasetEnabled && admAreaIds
            ? await handleAdmAreaOnOpenProject(admAreaIds)
            : {};

        const { adminAreaData: globalAdminAreaData } = globalAdminAreaIds
          ? await handleAdmAreaOnOpenProject(globalAdminAreaIds)
          : {};

        showToast({
          message: MESSAGE.OPEN_PROJECT_SUCCESS,
          type: EToastType.SUCCESS
        });

        const deletedDatasets = getDeletedDatasetsName(
          configurationDetail.datasets
        );
        showToast({
          message: deletedDatasets
            ? `Dataset "${deletedDatasets}" cannot be loaded, please check the dataset from data management`
            : MESSAGE.OPEN_PROJECT_SUCCESS,
          type: deletedDatasets ? EToastType.WARNING : EToastType.SUCCESS
        });

        onOpenConfigSuccess?.({
          ...configurationDetail,
          ...(datasetsData && { datasetsData }),
          ...(datasetsDetail && { datasetsDetail }),
          ...(catchmentDatasetData && { catchmentDatasetData }),
          ...(catchmentDatasetDetail && { catchmentDatasetDetail }),
          ...(adminAreaData && { adminAreaData }),
          ...(globalAdminAreaData && { globalAdminAreaData })
        });
      },
      onError: (error) => {
        console.error("Error fetching configuration:", error);
        showToast({
          type: EToastType.ERROR,
          message: MESSAGE.LOADING_CONFIGURATION_FAILED
        });
        onOpenConfigError?.(error);
      }
    });

  const { mutateAsync: mutateGetAnalysisConfig } = useGetAnalysisConfigMutation(
    {
      onSuccess: async (data, payload) => {
        const configurationDetail = JSON.parse(data.configuration);

        const globalAdminAreaIds = configurationDetail.adm_area_ids;
        const areaInput = configurationDetail.area_input;
        const catchmentDatasetId = areaInput?.catchment?.dataset?.id;
        const catchmentDatasetAttributes =
          areaInput?.catchment?.dataset?.attributes ?? [];
        const admAreaIds = getAreaInputAdmAreaIds(configurationDetail);
        const datasets = configurationDetail.datasets;
        const reversedDatasets = [...datasets].reverse();

        const datasetHistory: DatasetHistory = {
          datasets: {},
          rawDatasets: {},
          visualizationOrder: []
        };
        const datasetIndices: DatasetIndices = {};

        const { datasetsData, datasetsDetail, datasetsDetailAggregated } =
          await getDatasetDataAndMetadata(reversedDatasets);

        // index for reversedDatasets is used to align with datasetsData and datasetsDetail which are in reversed order, and for handle same dataset id with different filters or group_by in the same configuration
        for (const [index, dataset] of reversedDatasets.entries()) {
          const datasetMetadata: DatasetMetadata | undefined =
            datasetsDetail.find((d) => d.id === dataset.id);

          if (datasetMetadata) {
            const datasetWeights = mapDatasetWeights(
              dataset,
              datasetMetadata?.schema
            );

            await handleCreateDatasetHistory({
              datasetMetadata: {
                ...datasetMetadata, // full metadata schema
                display_name: dataset?.display_name ?? dataset.name
              },
              datasetData: datasetsData[index],
              savedFilters: buildDatasetFilter(
                dataset.attributes,
                datasetMetadata?.schema
                // Filters are stored top-to-bottom in project config, but dataset
                // history grows bottom-to-top (each entry prepended). Reversing
                // here aligns the filter order with the history display order.
              ).reverse(),
              attributesSchema: datasetsDetailAggregated[index]?.schema ?? [], // filtered metadata schema for h3 dataset
              h3Configuration: getAggregationConfig(dataset?.grid_config),
              group_by: dataset.group_by,
              weightValues: {
                // `dataset.weight` is 0 for projects saved before this field was
                // introduced. Fall back to the number of weighted attributes so
                // the weight panel initialises to a sensible non-zero value.
                weight: (
                  dataset.weight ||
                  Object.keys(datasetWeights).length ||
                  1
                ).toString(),
                attributes: datasetWeights,
                include_scoring: dataset?.include_scoring
              },
              datasetHistory,
              datasetIndices,
              styleConfig: {
                colorFamily: dataset?.color_scale,
                opacity: dataset?.opacity,
                selectedCategory: dataset?.color_based,
                labelConfig: dataset?.label_config,
                colorSteps: dataset?.color_steps,
                rankConfig: {
                  isEnabled: dataset.label_config?.enable_ranking ?? false,
                  maxRanking: dataset.label_config?.max_ranking ?? 10
                },
                strokeConfig:
                  dataset.spatial_aggregation_type === "line"
                    ? {
                        width: dataset?.stroke_width ?? 1,
                        opacity: dataset?.opacity ?? 0.7
                      }
                    : undefined
              },
              is_global_admin_area: dataset?.is_global_admin_area ?? false
            });
          }
        }
        dispatch(setDatasetHistory(datasetHistory));
        // The analysis config payload only carries `weight` values, not `percentage`.
        // After the dataset history is committed to the store, compute percentages
        // from the loaded weights so the weight panel shows the correct distribution.
        dispatch(recalculateWeightPercentages());

        const { catchmentDatasetData, catchmentDatasetDetail } =
          catchmentDatasetId
            ? await handleCatchmentDatasetOnOpenProject(
                catchmentDatasetId,
                catchmentDatasetAttributes
              )
            : {};

        const { adminAreaData } = admAreaIds
          ? await handleAdmAreaOnOpenProject(admAreaIds)
          : {};

        const { adminAreaData: globalAdminAreaData } = globalAdminAreaIds
          ? await handleAdmAreaOnOpenProject(globalAdminAreaIds)
          : {};

        const configResult = {
          ...configurationDetail,
          ...(datasetsData && { datasetsData }),
          ...(datasetsDetail && { datasetsDetail }),
          ...(catchmentDatasetData && { catchmentDatasetData }),
          ...(catchmentDatasetDetail && { catchmentDatasetDetail }),
          ...(adminAreaData && { adminAreaData }),
          ...(globalAdminAreaData && { globalAdminAreaData })
        };

        const spatialData = mapSpatialState(
          configResult,
          addFeature,
          payload?.analysisId ?? spatial.latestAnalysisId ?? undefined,
          isAdminTreeEnabled
        );

        // Preserve local polygon state on post-analysis refetch only:
        // BE doesn't persist polygon for catchment+BY_POLYGON, so the BE
        // response returns spatial state without it. `latestAnalysisId`
        // gates this to the post-analysis flow (it's set when an analysis
        // has been started in the current session).
        const shouldPreservePolygonCatchment =
          !!latestAnalysisId &&
          isBVTDataForCatchmentEnabled &&
          spatial.catchmentSettings?.catchmentInputBy ===
            ECatchmentInputBy.BY_POLYGON &&
          Object.keys(spatial.polygonSettings ?? {}).length > 0;

        dispatch(
          setSpatialState({
            ...spatialData,
            ...(shouldPreservePolygonCatchment && {
              polygonSettings: spatial.polygonSettings,
              catchmentSettings: spatial.catchmentSettings,
              spatialConfig: spatial.spatialConfig
            })
          })
        );

        if (isAdminTreeEnabled) {
          if (configResult.globalAdminAreaData?.selectValue?.lv0) {
            dispatch(
              setGlobalAdminAreaSettings({
                lv0: configResult.globalAdminAreaData?.selectValue?.lv0
              })
            );
          }
          dispatch(
            setGlobalAdminAreaSettingsData(
              configResult.globalAdminAreaData?.geoData ?? null
            )
          );
          dispatch(setGlobalSelectedAreaIds(configResult.adm_area_ids || []));
        } else {
          dispatch(setGlobalAdminArea(configResult.globalAdminAreaData));
        }
      }
    }
  );

  return {
    mutateSaveConfig: handleSaveConfiguration,
    mutateOpenConfig,
    mutateGetAnalysisConfig,
    configurationDetailData // TODO: to be deleted after refactor
  };
};
